package com.bikompanion.data.cache

import dev.zacsweers.metro.Inject
import kotlinx.serialization.json.Json
import com.bikompanion.data.BikeDataSource
import com.bikompanion.domain.BikeNetwork
import com.bikompanion.domain.BikeParkingSpot
import com.bikompanion.domain.BikeStation
import com.bikompanion.domain.BoundingBox
import com.bikompanion.domain.CoveragePoint
import com.bikompanion.domain.NetworkCoverage
import com.bikompanion.domain.ParkingType
import kotlin.time.Clock
import kotlin.time.ExperimentalTime

/**
 * Bumped whenever the stored shape changes, so an old cache is ignored rather than failing to
 * parse on the first launch after an update.
 *
 * Also the lever for "an old cache is ignored" when nothing about the *shape* changed but the
 * bundled dataset did: a cached rack list from before [ParkingType] existed would otherwise sit
 * for up to seven days serving [ParkingType.Other] for everything, no matter how fresh the
 * rebuilt dataset is — the cache is what BundledParkingRepository is asked through, not bypassed.
 */
private const val CACHE_VERSION = "v6"

private val cacheJson = Json { ignoreUnknownKeys = true }

/**
 * The last good copy of what the API told us, kept across launches.
 *
 * Its own DTOs rather than the domain types: the domain stays free of serialization, and a
 * field added to [BikeStation] tomorrow can't silently change the on-disk format.
 *
 * Keyed by data source as well as by network, because the two sources disagree about what a
 * network contains — switching between them must not read the other one's numbers back.
 */
@OptIn(ExperimentalTime::class)
@Inject
class BikeDataCache(
    private val store: LocalStore,
) {
    fun readNetworks(): List<BikeNetwork>? = read<CachedNetworks>(networksKey())?.networks?.map { it.toDomain() }

    /** How long ago the cached network list was written, or `null` if there is none. */
    fun readNetworksAge(): Long? =
        read<CachedNetworks>(networksKey())?.let { Clock.System.now().toEpochMilliseconds() - it.updatedAtEpochMillis }

    fun writeNetworks(
        networks: List<BikeNetwork>,
        updatedAtEpochMillis: Long = Clock.System.now().toEpochMilliseconds(),
    ) {
        write(networksKey(), CachedNetworks(networks.map { it.toCached() }, updatedAtEpochMillis))
    }

    fun readStations(networkId: String): StationsCacheEntry? =
        read<CachedStations>(stationsKey(networkId))?.let { cached ->
            StationsCacheEntry(
                stations = cached.stations.map { it.toDomain() },
                updatedAtEpochMillis = cached.updatedAtEpochMillis,
            )
        }

    fun writeStations(
        networkId: String,
        stations: List<BikeStation>,
        updatedAtEpochMillis: Long,
    ) {
        write(
            stationsKey(networkId),
            CachedStations(updatedAtEpochMillis, stations.map { it.toCached() }),
        )
    }

    fun readParking(areaKey: String): ParkingCacheEntry? =
        read<CachedParking>(parkingKey(areaKey))?.let { cached ->
            ParkingCacheEntry(
                spots = cached.spots.map { it.toDomain() },
                updatedAtEpochMillis = cached.updatedAtEpochMillis,
            )
        }

    fun writeParking(
        areaKey: String,
        spots: List<BikeParkingSpot>,
        updatedAtEpochMillis: Long = Clock.System.now().toEpochMilliseconds(),
    ) {
        write(parkingKey(areaKey), CachedParking(updatedAtEpochMillis, spots.map { it.toCached() }))
        pruneParking(justWritten = areaKey, writtenAt = updatedAtEpochMillis)
    }

    /**
     * Keeps the newest [MaxCachedParkingAreas] areas and drops the rest.
     *
     * Racks are the one thing here stored per area rather than per network, so nothing bounds how
     * many entries a rider who travels accumulates. On the web that ends with `localStorage` full,
     * every write failing silently, and the map refetching everything each session.
     *
     * Evicted by recency rather than by age: an area past its lifetime is still the fallback shown
     * when Overpass is down, and throwing it out on its birthday would trade a slightly stale map
     * for a blank one.
     *
     * The sweep goes by stored key rather than by the index, so entries written under an earlier
     * [CACHE_VERSION] — unreachable through [parkingKey], and otherwise permanent — go too.
     */
    private fun pruneParking(
        justWritten: String,
        writtenAt: Long,
    ) {
        val kept = (read<CachedParkingIndex>(parkingIndexKey())?.areas.orEmpty() + (justWritten to writtenAt))
            .entries
            .sortedByDescending { it.value }
            .take(MaxCachedParkingAreas)
            .associate { it.key to it.value }
        write(parkingIndexKey(), CachedParkingIndex(kept))
        val keep = kept.keys.mapTo(mutableSetOf(), ::parkingKey) + parkingIndexKey()
        runCatching {
            store
                .keys()
                .filter { it.startsWith(ParkingKeyPrefix) && it !in keep }
                .forEach { store.remove(it) }
        }
    }

    private inline fun <reified T> read(key: String): T? =
        runCatching {
            store.read(key)?.let { cacheJson.decodeFromString<T>(it) }
        }.getOrNull()

    private inline fun <reified T> write(
        key: String,
        value: T,
    ) {
        runCatching { store.write(key, cacheJson.encodeToString(value)) }
    }

    private fun networksKey() = cacheKey(source, "networks", CACHE_VERSION)

    private fun stationsKey(networkId: String) = cacheKey(source, "stations", networkId, CACHE_VERSION)

    /**
     * Deliberately outside [source]: racks come from OpenStreetMap, not from whichever bike
     * feed is active, so switching feeds must not throw away an area already fetched.
     */
    private fun parkingKey(areaKey: String) = cacheKey("osm", "parking", areaKey, CACHE_VERSION)

    /** Which areas are stored and when each was written, so a sweep needs no decoding. */
    private fun parkingIndexKey() = cacheKey("osm", "parking", "index", CACHE_VERSION)

    private val source: String get() = BikeDataSource.Active.name
}

private fun BikeNetwork.toCached() =
    CachedNetwork(
        id = id,
        name = name,
        latitude = latitude,
        longitude = longitude,
        city = city,
        country = country,
        company = company,
        href = href,
        licenseName = licenseName,
        licenseUrl = licenseUrl,
        coverage = coverage?.let { area ->
            CachedCoverage(area.bounds.toCached(), area.points.map { it.toCached() })
        },
    )

private fun BoundingBox.toCached() = CachedBox(south, west, north, east)

private fun CachedBox.toDomain() = BoundingBox(south, west, north, east)

private fun CoveragePoint.toCached() = CachedPoint(latitude, longitude)

private fun CachedPoint.toDomain() = CoveragePoint(latitude, longitude)

private fun CachedNetwork.toDomain() =
    BikeNetwork(
        id = id,
        name = name,
        latitude = latitude,
        longitude = longitude,
        city = city,
        country = country,
        company = company,
        href = href,
        licenseName = licenseName,
        licenseUrl = licenseUrl,
        coverage = coverage?.let { area ->
            NetworkCoverage(area.bounds.toDomain(), area.points.map { it.toDomain() })
        },
    )

/** The most areas of racks kept on disk at once. See `pruneParking`. */
private const val MaxCachedParkingAreas = 24

/** Shared by every parking entry, across cache versions, so a sweep can find the stale ones. */
private val ParkingKeyPrefix = cacheKey("osm", "parking")

private fun BikeParkingSpot.toCached() =
    CachedParkingSpot(
        id = id,
        latitude = latitude,
        longitude = longitude,
        capacity = capacity,
        isCovered = isCovered,
        address = address,
        type = type.name,
    )

private fun CachedParkingSpot.toDomain() =
    BikeParkingSpot(
        id = id,
        latitude = latitude,
        longitude = longitude,
        capacity = capacity,
        isCovered = isCovered,
        address = address,
        // A name a future rebuild no longer writes, or that pre-dates this field, reads as Other
        // rather than failing the whole entry to decode.
        type = ParkingType.entries.firstOrNull { it.name == type } ?: ParkingType.Other,
    )

private fun BikeStation.toCached() =
    CachedStation(
        id = id,
        name = name,
        networkId = networkId,
        latitude = latitude,
        longitude = longitude,
        freeBikes = freeBikes,
        emptySlots = emptySlots,
        electricBikes = electricBikes,
        supportsElectricBikes = supportsElectricBikes,
    )

private fun CachedStation.toDomain() =
    BikeStation(
        id = id,
        name = name,
        networkId = networkId,
        latitude = latitude,
        longitude = longitude,
        freeBikes = freeBikes,
        emptySlots = emptySlots,
        electricBikes = electricBikes,
        supportsElectricBikes = supportsElectricBikes,
    )
